// redux/slices/ordersSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";
import { resetStore } from "./resetStore";

const axiosRequest = async ({
  url,
  method = "GET",
  data,
  raw = false,
}) => {
  const jwt = await getJwtToken();

  const config = {
    url: `${getServerUrl()}${url}`,
    method,
    headers: {
      "Content-Type": "application/json",
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    validateStatus: raw ? () => true : undefined,
  };

  if (data !== undefined) {
    config.data = data;
  }

  const response = await axios(config);
  return raw ? response : response.data;
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

export const createOrder = createAsyncThunk(
  "orders/createOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const data = await axiosRequest({
        url: "/orders",
        method: "POST",
        data: orderData,
      });

      return data?.order || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to create order"));
    }
  }
);

export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await axiosRequest({
        url: `/orders/${uid}`,
        method: "GET",
        raw: true,
      });

      if (response.status === 404 || response.status === 403) return [];

      if (response.status !== 200) {
        return rejectWithValue(`Failed to fetch orders (HTTP ${response.status})`);
      }

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch orders"));
    }
  }
);

export const updateOrder = createAsyncThunk(
  "orders/updateOrder",
  async ({ id, orderData }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID заказа не передан");

      const data = await axiosRequest({
        url: `/orders/${id}`,
        method: "PUT",
        data: orderData,
      });

      return data?.order || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to update order"));
    }
  }
);

export const deleteOrder = createAsyncThunk(
  "orders/deleteOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      if (!orderId) throw new Error("ID заказа не передан");

      const response = await axiosRequest({
        url: `/orders/${orderId}`,
        method: "DELETE",
        raw: true,
      });

      if (response.status !== 200) {
        throw new Error(
          response.data?.error ||
          response.data?.message ||
          `Failed to delete order (HTTP ${response.status})`
        );
      }

      return orderId;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to delete order"));
    }
  }
);

export const fetchOrderItems = createAsyncThunk(
  "orders/fetchOrderItems",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не передан");

      const response = await axiosRequest({
        url: `/orders/items?uid=${encodeURIComponent(uid)}`,
        method: "GET",
        raw: true,
      });

      if (response.status === 404 || response.status === 403) return [];

      if (response.status !== 200) {
        return rejectWithValue(`Ошибка загрузки: ${response.status}`);
      }

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Failed to fetch order items"));
    }
  }
);

export const updateOrderItemDoc = createAsyncThunk(
  "orders/updateOrderItemDoc",
  async ({ id, patch }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID товара заказа не передан");

      const data = await axiosRequest({
        url: `/orders/items/${id}/doc`,
        method: "PUT",
        data: patch,
      });

      return data?.item || data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update order item document")
      );
    }
  }
);

export const updateOrderPaidStatus = createAsyncThunk(
  "orders/updateOrderPaidStatus",
  async ({ id, isPaid }, { rejectWithValue }) => {
    try {
      if (!id) throw new Error("ID заказа не передан");

      const data = await axiosRequest({
        url: `/orders/${id}/is-paid`,
        method: "PUT",
        data: { isPaid },
      });

      return data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update order paid status")
      );
    }
  }
);

export const fetchOrderByAppointmentId = createAsyncThunk(
  "orders/fetchOrderByAppointmentId",
  async (appointmentId, { rejectWithValue }) => {
    try {
      if (!appointmentId) throw new Error("appointmentId не передан");

      const response = await axiosRequest({
        url: `/orders/appointment/${appointmentId}`,
        method: "GET",
        raw: true,
      });

      if (response.status === 404) {
        return { order: null, items: [] };
      }

      if (response.status !== 200) {
        return rejectWithValue(
          `Failed to fetch order by appointment (HTTP ${response.status})`
        );
      }

      return response.data || { order: null, items: [] };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch order by appointment")
      );
    }
  }
);

export const deleteOrderByAppointmentId = createAsyncThunk(
  "orders/deleteOrderByAppointmentId",
  async (appointmentId, { rejectWithValue }) => {
    try {
      if (!appointmentId) throw new Error("appointmentId не передан");

      const response = await axiosRequest({
        url: `/orders/appointment/${appointmentId}`,
        method: "DELETE",
        raw: true,
      });

      if (response.status !== 200) {
        throw new Error(
          response.data?.error ||
          response.data?.message ||
          `Failed to delete order (HTTP ${response.status})`
        );
      }

      return appointmentId;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to delete order by appointment")
      );
    }
  }
);

const initialState = {
  orders: [],
  orderItems: [],
  appointmentOrder: null,
  appointmentOrderItems: [],
  status: "idle",
  error: null,
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
};

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetStatuses: (state) => {
      state.status = "idle";
      state.createStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
      state.error = null;
    },
    clearAppointmentOrder: (state) => {
      state.appointmentOrder = null;
      state.appointmentOrderItems = [];
    },
    clearOrders: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.orders = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(createOrder.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(createOrder.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        if (action.payload) state.orders.push(action.payload);
      })
      .addCase(createOrder.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(updateOrder.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";

        const updated = action.payload;
        if (!updated?.id) return;

        const index = state.orders.findIndex(
          (order) => String(order.id) === String(updated.id)
        );

        if (index !== -1) {
          state.orders[index] = updated;
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(deleteOrder.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.error = null;

        const orderId = String(action.payload);

        state.orders = (state.orders || []).filter(
          (order) => String(order?.id) !== orderId
        );

        state.orderItems = (state.orderItems || []).filter(
          (item) =>
            String(
              item?.orderId ??
              item?.order_id ??
              item?.order?.id
            ) !== orderId
        );
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(fetchOrderItems.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchOrderItems.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.orderItems = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchOrderItems.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(updateOrderItemDoc.fulfilled, (state, action) => {
        const updated = action.payload;
        if (!updated?.id) return;

        const index = state.orderItems.findIndex(
          (item) => String(item.id) === String(updated.id)
        );

        if (index !== -1) {
          state.orderItems[index] = {
            ...state.orderItems[index],
            ...updated,
          };
        }
      })

      .addCase(updateOrderPaidStatus.fulfilled, (state, action) => {
        const { id, isPaid } = action.payload || {};

        const index = state.orders.findIndex(
          (order) => String(order.id) === String(id)
        );

        if (index !== -1) {
          state.orders[index].isPaid = !!isPaid;
        }

        if (String(state.appointmentOrder?.id) === String(id)) {
          state.appointmentOrder.isPaid = !!isPaid;
        }
      })

      .addCase(fetchOrderByAppointmentId.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.appointmentOrder = null;
        state.appointmentOrderItems = [];
      })
      .addCase(fetchOrderByAppointmentId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.appointmentOrder = action.payload?.order || null;
        const items =
          action.payload?.items ??
          action.payload?.orderItems ??
          action.payload?.order?.items;
        state.appointmentOrderItems = Array.isArray(items) ? items : [];
      })
      .addCase(fetchOrderByAppointmentId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
        state.appointmentOrder = null;
        state.appointmentOrderItems = [];
      })

      .addCase(deleteOrderByAppointmentId.fulfilled, (state) => {
        state.appointmentOrder = null;
        state.appointmentOrderItems = [];
      })

      .addCase(resetStore, () => initialState);
  },
});

export const {
  clearError,
  resetStatuses,
  clearAppointmentOrder,
  clearOrders,
} = orderSlice.actions;

export const selectOrders = (state) => state.orders?.orders || [];
export const selectOrderItems = (state) => state.orders?.orderItems || [];
export const selectAppointmentOrder = (state) =>
  state.orders?.appointmentOrder || null;
export const selectAppointmentOrderItems = (state) =>
  state.orders?.appointmentOrderItems || [];
export const selectOrdersStatus = (state) => state.orders?.status || "idle";
export const selectOrdersError = (state) => state.orders?.error || null;

export default orderSlice.reducer;
